/**
 * The query controls.
 *
 * D7 makes this the entry point, not the lens list: you arrive knowing a
 * constraint ("under 500 g", "at least f/2.8") and the list is the answer. So
 * sort sits at the top as a first-class control rather than hiding in a column
 * header, and filters are the main event rather than a refinement.
 *
 * REQ-WEB-019 governs the shape: controls **wrap or collapse behind a
 * disclosure**, never a horizontally scrolling strip. The prototype put filters
 * in their own scroll container and the owner could not tell whether a swipe
 * was moving the filters or the page — and nearly missed that the results were
 * a page-scroll away. Everything here lives in normal page flow. There is one
 * scroll context on this page and it is the page.
 */
import noUiSlider from "nouislider";
import type { API as NoUiSliderApi } from "nouislider";
import "nouislider/dist/nouislider.css";
import type { JSX } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";

import type { FilterOptions, Attribute, MountList, Range, Registry } from "./api";
import { api, toCanonical, toDisplay } from "./api";
import type { Camera } from "./camera";
import { humaniseComputed } from "./formula";
import { options as cameraOptions, smallerCoverage, smallerFormatName } from "./camera";
import { formatNumber, formatPartialDate, UNKNOWN, withUnit } from "./format";
import { buildRange } from "./range-scale";
import type { Op, Query } from "./query";
import {
  byGroupThenName,
  filtersFor,
  toSearch,
  IDENTITY_FILTERS,
  isComputed,
  shownLabel,
  shortLabel,
  withFilter,
  withoutFilter,
} from "./query";
import type { IdentityFilter } from "./query";

/** A range built from too few values describes noise, not the field. */
const MIN_POINTS_FOR_RANGE = 3;

// ---------------------------------------------------------------------------

export function Disclosure(props: {
  label: string;
  detail?: string | undefined;
  open: boolean;
  onToggle: () => void;
  children: JSX.Element | JSX.Element[];
}) {
  const id = useId();
  return (
    <section class={props.open ? "disclosure is-open" : "disclosure"}>
      <button
        type="button"
        class="disclosure-summary"
        aria-expanded={props.open}
        aria-controls={id}
        onClick={props.onToggle}
      >
        <span class="disclosure-chevron" aria-hidden="true" />
        <span class="disclosure-label">{props.label}</span>
        {props.detail ? <span class="disclosure-detail">{props.detail}</span> : null}
      </button>
      <div id={id} class="disclosure-body" hidden={!props.open}>
        {props.children}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

export function SortControl(props: {
  registry: Registry;
  query: Query;
  onChange: (q: Query) => void;
}) {
  const { registry, query } = props;
  const sortable = registry.attributes.filter((a) => a.is_sortable);
  const current = sortable.find((a) => a.key === query.sortKey);

  return (
    <div class="sortbar">
      <label class="visually-hidden" for="sort-key">
        Sort by
      </label>
      <span class="sortbar-label" aria-hidden="true">
        Sort
      </span>
      <select
        id="sort-key"
        class="select"
        value={query.sortKey}
        onChange={(e) => {
          const key = (e.target as HTMLSelectElement).value;
          // A date opens newest first (REQ-WEB-042): the order a shopper
          // expects. The direction button still flips it to oldest first.
          const toDate = sortable.find((a) => a.key === key)?.datatype === "date";
          props.onChange({ ...query, sortKey: key, ...(toDate ? { sortDesc: true } : {}) });
        }}
      >
        {/* "name" is the name each result shows, maker then model (#215). */}
        <option value="name">Name (A–Z)</option>
        {/* Computed columns sort like stored ones (REQ-FX-016), so they belong
            in the same control rather than in a separate one that would imply
            they are a lesser kind of column. Ordering is server-side over the
            whole result set (REQ-FX-010). */}
        {query.computed.length ? (
          <optgroup label="Calculated columns">
            {query.computed.map((c) => (
              <option key={c.name} value={c.name}>
                {humaniseComputed(c.name)}
              </option>
            ))}
          </optgroup>
        ) : null}
        {byGroupThenName(sortable).map(([group, attrs]) => (
          <optgroup key={group} label={titleCase(group)}>
            {attrs.map((a) => (
              <option key={a.key} value={a.key}>
                {shownLabel(a, a.key)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <button
        type="button"
        class="direction"
        onClick={() => props.onChange({ ...query, sortDesc: !query.sortDesc })}
        aria-label={
          current?.datatype === "date"
            ? query.sortDesc
              ? `Sorted newest first. Switch to oldest first.`
              : `Sorted oldest first. Switch to newest first.`
            : query.sortDesc
              ? `Sorted highest first. Switch to lowest first.`
              : `Sorted lowest first. Switch to highest first.`
        }
        title={
          current?.datatype === "date"
            ? query.sortDesc ? "Newest first" : "Oldest first"
            : query.sortDesc ? "Highest first" : "Lowest first"
        }
      >
        <span aria-hidden="true">{query.sortDesc ? "↓" : "↑"}</span>
        <span class="direction-text">
          {current?.datatype === "date"
            ? query.sortDesc
              ? "newest first"
              : "oldest first"
            : isComputed(query, query.sortKey)
            ? query.sortDesc
              ? "high → low"
              : "low → high"
            : current?.datatype === "numeric" || current?.datatype === "integer"
            ? query.sortDesc
              ? "high → low"
              : "low → high"
            : query.sortDesc
              ? "Z → A"
              : "A → Z"}
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A two-handle range slider, drawn and moved by noUiSlider (#414).
 *
 * The widget is a library on purpose. The previous one was homemade (two native
 * range inputs and our own gesture code) and nearly every QA pass found a bug in
 * it; noUiSlider is built for exactly this, with touch, keyboard and ARIA
 * handled and a non-linear `range` for log scales. What stays ours is everything
 * around it: the bounds seeded from the data, the readouts, the URL.
 *
 * Ownership, which is the whole design:
 *
 * - **During a gesture the widget owns the thumbs.** They are never written back
 *   from the app's state, so nothing can pull a thumb out from under a finger
 *   (what made #408's rounding jumpy: state, rounded, fed back into the control).
 * - **Rounding happens once, on the way out** (`shown`): the readout and the
 *   address bar get the same rounded number, so they cannot disagree (#399,
 *   REQ-WEB-003). The thumb is not rounded: with `smooth-steps` it follows the
 *   finger exactly and settles onto a step when released.
 * - **The app's state flows back in only when it did not come from here** (the
 *   back button, a removed filter), and never while a move is still echoing.
 */
function DualRange(props: {
  attr: Attribute;
  range: Range;
  lo: number;
  hi: number;
  onChange: (lo: number, hi: number) => void;
}) {
  const { attr, range } = props;
  // Bounds arrive canonical; every number the user sees and touches is display.
  //
  // The track spans the FULL observed range (REQ-WEB-029 as amended). An
  // earlier version narrowed it to p1..p99 and was measured before shipping:
  // aperture's p99 is f/10.16, which puts the Canon RF 600mm f/11 and 800mm
  // f/11 — ordinary lenses — outside the track, while the only real anomalies
  // are two pinholes above f/16. The scale below already buys the width the
  // percentile was there to buy, so nothing is off the track at all.
  const min = toDisplay(attr, range.min ?? 0);
  const max = toDisplay(attr, range.max ?? 0);
  const lo = clamp(props.lo, min, max);
  const hi = clamp(props.hi, min, max);

  // Aperture is a geometric series and so is focal length; the registry says
  // which (REQ-DM-022) and nothing here knows an attribute key. A log track
  // gives every stop — every equal ratio — equal width (range-scale.ts).
  const geometric = attr.scale === "geometric" && min > 0 && max > min;

  // The readout's precision: the registry's step, or a fallback. A computed
  // column has no registry step, and formatting with `null` meant zero
  // decimals, so a density of 0.025-0.232 read "0 – 0" while the filter itself
  // worked (#194).
  const readStep = attr.display_step ?? niceStep(min, max);

  /** What the readout, the address bar and the screen reader all say. */
  const shown = (v: number) =>
    v <= min ? min : v >= max ? max : clamp(Number(formatNumber(v, readStep)), min, max);
  const said = (v: number) => withUnit(attr, formatNumber(v, readStep));

  const elRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<NoUiSliderApi | null>(null);
  /** The latest of everything the long-lived widget callbacks need. */
  const latest = useRef({ lo, hi, shown, said, onChange: props.onChange });
  latest.current = { lo, hi, shown, said, onChange: props.onChange };
  /** The last pair this control handed out, and until when its echo is expected. */
  const sent = useRef<{ lo: number; hi: number; until: number } | null>(null);
  const [tick, setTick] = useState(0);

  const trackKey = `${attr.key}|${attr.label}|${min}|${max}|${geometric}|${readStep}`;
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const slider = noUiSlider.create(el, {
      start: [latest.current.lo, latest.current.hi],
      connect: true,
      range: buildRange(min, max, geometric, readStep),
      // Plain numbers in and out: formatting is ours (`shown`, `said`).
      format: { to: (v: number) => v, from: (v: string) => Number(v) },
      // `smooth-steps`: during a drag the thumb is exactly under the finger; it
      // settles onto a step on release. Arrow keys always move one step
      // (REQ-WEB-009, D30). No `tap`/`snap`: those make a press on the bare
      // track move a thumb at touch-down, before the browser knows whether the
      // finger is scrolling the page (REQ-WEB-019). Only a handle is draggable.
      behaviour: "smooth-steps",
      // One name per handle, and the value as the readout says it (not a step
      // index or a bare number, #201).
      handleAttributes: [
        { "aria-label": `Minimum ${attr.label}` },
        { "aria-label": `Maximum ${attr.label}` },
      ],
      ariaFormat: { to: (v: number) => latest.current.said(latest.current.shown(v)) },
    });
    sliderRef.current = slider;

    const emit = (_v: unknown, _h: number, u: number[]) => {
      const { shown: sh, onChange } = latest.current;
      const nlo = sh(u[0]!);
      const nhi = sh(u[1]!);
      const was = sent.current;
      if (was && was.lo === nlo && was.hi === nhi) return;
      // The echo of this value reaches the props a moment later; until then the
      // props are older than the widget and must not be written back into it.
      sent.current = { lo: nlo, hi: nhi, until: Date.now() + 400 };
      onChange(nlo, nhi);
    };
    slider.on("slide", emit);
    // Release, a tap and the arrow keys end on a step: the final value is the
    // snapped one, which a smooth drag has not reported yet.
    slider.on("change", emit);

    // WHICH HANDLE A PRESS GRABS, WHEN THEY ARE CLOSE (REQ-WEB-021, -017).
    //
    // Each handle's touch box is 44 px wide (REQ-WEB-017), so two handles less
    // than 44 px apart have overlapping boxes, and noUiSlider settles who is on
    // top with `z-index` alone (setZindex in its source): in the right half of
    // the track the MINIMUM handle sits above the maximum one, so pressing the
    // centre of the maximum handle, with the minimum 11-21 px to its left, took
    // the minimum. The library has no option for this. So the two boxes are
    // trimmed to meet at the midpoint between the handles: the side of the
    // midpoint a press lands on decides which handle moves, and at distance 0
    // each handle owns its own half. The visible thumb is not touched.
    const handles = Array.from(el.querySelectorAll<HTMLElement>(".noUi-handle"));
    const HIT = 44;
    const trimTouchBoxes = () => {
      const [h0, h1] = handles;
      if (!h0 || !h1) return;
      // The origin is the handle's parent; its right edge is the value's position.
      const gap =
        h1.parentElement!.getBoundingClientRect().right -
        h0.parentElement!.getBoundingClientRect().right;
      const w = HIT / 2 + Math.min(HIT / 2, Math.max(0, gap) / 2);
      h0.style.width = `${w}px`;
      h0.style.right = `${22 - w}px`; // keeps its left edge, 22 px left of its centre
      h0.style.setProperty("--thumb-left", "11px");
      h1.style.width = `${w}px`;
      h1.style.right = "-22px"; // keeps its right edge, 22 px right of its centre
      h1.style.setProperty("--thumb-left", `${w - 33}px`);
    };
    trimTouchBoxes();
    slider.on("update", trimTouchBoxes);
    const watcher = new ResizeObserver(trimTouchBoxes);
    watcher.observe(el);

    // What a screen reader is told about the range of each handle, in the
    // readout's own precision. The library writes these to one decimal, which
    // reads "0.0" for a value the readout says is "0.01 m" (REQ-WEB-003).
    const tellReaders = (_v: unknown, _h: number, u: number[]) => {
      const { shown: sh } = latest.current;
      const num = (v: number) => formatNumber(sh(v), readStep);
      const vals = [sh(u[0]!), sh(u[1]!)];
      handles.forEach((h, i) => {
        h.setAttribute("aria-valuemin", num(i === 0 ? min : vals[0]!));
        h.setAttribute("aria-valuemax", num(i === 0 ? vals[1]! : max));
        h.setAttribute("aria-valuenow", num(vals[i]!));
      });
    };
    tellReaders(null, 0, slider.get(true) as number[]);
    slider.on("update", tellReaders);

    return () => {
      watcher.disconnect();
      slider.destroy();
      sliderRef.current = null;
    };
    // The track's inputs are all in trackKey; the callbacks read `latest`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackKey]);

  // The app's state, back into the widget: only when it did not come from here.
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    const now = Date.now();
    const was = sent.current;
    if (was && now < was.until) {
      // Still inside the echo window of our own last move: look again once it closes.
      const t = setTimeout(() => setTick((n) => n + 1), was.until - now + 20);
      return () => clearTimeout(t);
    }
    const cur = slider.get(true) as number[];
    const same = (a: number, b: number) => Math.abs(shown(a) - shown(b)) < readStep / 1000;
    if (same(cur[0]!, lo) && same(cur[1]!, hi)) return;
    // exactInput: put the thumb where the value is, not on the nearest step.
    slider.set([lo, hi], false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lo, hi, tick, trackKey]);

  return (
    <div class="range">
      <div class="range-readout">
        {/* The unit goes wherever that unit belongs — "f/2.8", not "2.8 f".
            Shared with the results table so one quantity is spelled one way. */}
        <output>{said(lo)}</output>
        <span class="range-dash" aria-hidden="true">
          –
        </span>
        <output>{said(hi)}</output>
      </div>

      {/* noUiSlider fills this in: the track, both handles, the bar between. */}
      <div class="range-track" ref={elRef} />

      <div class="range-bounds" aria-hidden="true">
        <span>{said(min)}</span>
        <span>{said(max)}</span>
      </div>
    </div>
  );
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/** A step for attributes the registry did not give one: ~200 stops across the span. */
function niceStep(min: number, max: number): number {
  const span = Math.abs(max - min) || 1;
  const raw = span / 200;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  return Math.max(mag, 0.001);
}

// ---------------------------------------------------------------------------

/**
 * How a change to the query reached the app. `"slide"` marks a slider moving,
 * which REQ-WEB-023 keeps off the back stack; every other change is a finished
 * choice.
 */
export type QueryChange = (q: Query, how?: "slide") => void;

function FilterRow(props: {
  attr: Attribute;
  range: Range | undefined;
  query: Query;
  onChange: QueryChange;
  onRemove: () => void;
}) {
  const { attr, range, query } = props;
  const active = filtersFor(query, attr.key);

  const set = (op: Op, value: string) => props.onChange(withFilter(query, { key: attr.key, op, value }));
  const clear = (op: Op) => props.onChange(withoutFilter(query, attr.key, op));

  let body: JSX.Element;

  if (attr.datatype === "numeric" || attr.datatype === "integer") {
    if (!range || range.min === null || range.max === null) {
      body = (
        <p class="filter-empty">
          Nobody has cited this yet, so there is nothing to filter on.
        </p>
      );
    } else if (range.count < MIN_POINTS_FOR_RANGE || range.min === range.max) {
      body = (
        <p class="filter-empty">
          Only {range.count} {range.count === 1 ? "lens has" : "lenses have"} this spec so far, too
          few to make a range.
        </p>
      );
    } else {
      const dMin = toDisplay(attr, range.min);
      const dMax = toDisplay(attr, range.max);
      const lo = active.gte !== undefined ? toDisplay(attr, Number(active.gte)) : dMin;
      const hi = active.lte !== undefined ? toDisplay(attr, Number(active.lte)) : dMax;
      body = (
        <DualRange
          attr={attr}
          range={range}
          lo={lo}
          hi={hi}
          onChange={(nlo, nhi) => {
            // Only send a bound that actually constrains. A filter pinned to the
            // dataset's own edge excludes nothing and would clutter the URL.
            let next = query;
            next =
              nlo > dMin
                ? withFilter(next, { key: attr.key, op: "gte", value: String(toCanonical(attr, nlo)) })
                : withoutFilter(next, attr.key, "gte");
            next =
              nhi < dMax
                ? withFilter(next, { key: attr.key, op: "lte", value: String(toCanonical(attr, nhi)) })
                : withoutFilter(next, attr.key, "lte");
            props.onChange(next, "slide");
          }}
        />
      );
    }
  } else if (attr.datatype === "date") {
    // REQ-WEB-042: a year range. The span is in whole years and so are the
    // bounds sent ("released 2020 or later" is gte=2020, "2023 or earlier" is
    // lte=2023); the API counts a year-only date as that year. The same range
    // control as a numeric spec, fed years.
    if (!range || range.min === null || range.max === null || range.count === 0) {
      body = (
        <p class="filter-empty">
          Nobody has cited a release date yet, so there is nothing to filter on.
        </p>
      );
    } else if (range.count < MIN_POINTS_FOR_RANGE || range.min === range.max) {
      body = (
        <p class="filter-empty">
          Only {range.count} {range.count === 1 ? "lens has" : "lenses have"} a release date so
          far, too few to make a range.
        </p>
      );
    } else {
      const yearAttr: Attribute = {
        ...attr, datatype: "integer", unit_family: "none", display_unit: null,
        display_factor: null, display_step: 1, scale: "linear",
      };
      const dMin = range.min;
      const dMax = range.max;
      const yearOf = (v: string | undefined, fallback: number) => {
        const n = v === undefined ? NaN : Number(v.slice(0, 4));
        return Number.isFinite(n) ? n : fallback;
      };
      body = (
        <DualRange
          attr={yearAttr}
          range={range}
          lo={yearOf(active.gte, dMin)}
          hi={yearOf(active.lte, dMax)}
          onChange={(nlo, nhi) => {
            // Only a bound that constrains is sent, as for a number.
            let next = query;
            next = nlo > dMin
              ? withFilter(next, { key: attr.key, op: "gte", value: String(nlo) })
              : withoutFilter(next, attr.key, "gte");
            next = nhi < dMax
              ? withFilter(next, { key: attr.key, op: "lte", value: String(nhi) })
              : withoutFilter(next, attr.key, "lte");
            props.onChange(next, "slide");
          }}
        />
      );
    }
  } else if (attr.datatype === "boolean") {
    const v = active.eq;
    body = (
      <div class="tri" role="group" aria-label={attr.label}>
        {[
          { label: "Any", val: undefined },
          { label: "Yes", val: "true" },
          { label: "No", val: "false" },
        ].map((o) => (
          <button
            key={o.label}
            type="button"
            class="tri-option"
            aria-pressed={v === o.val}
            onClick={() => (o.val === undefined ? clear("eq") : set("eq", o.val))}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  } else if (attr.datatype === "enum" && attr.options_ordered) {
    // REQ-DM-029: the levels run least to most protected, so the question a
    // shopper asks is "this much or better". One choice, sent as gte; the
    // server compares by the options' own order.
    const level = active.gte;
    body = (
      <div class="chips" role="group" aria-label={`${attr.label}, this level or better`}>
        {attr.options.map((o) => (
          <button
            key={o.value}
            type="button"
            class="chip"
            aria-pressed={level === o.value}
            onClick={() => (level === o.value ? clear("gte") : set("gte", o.value))}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  } else if (attr.datatype === "enum") {
    const chosen = new Set((active.in ?? "").split(",").filter(Boolean));
    body = (
      <div class="chips" role="group" aria-label={attr.label}>
        {attr.options.map((o) => (
          <button
            key={o.value}
            type="button"
            class="chip"
            aria-pressed={chosen.has(o.value)}
            onClick={() => {
              const next = new Set(chosen);
              if (next.has(o.value)) next.delete(o.value);
              else next.add(o.value);
              if (next.size === 0) clear("in");
              else set("in", [...next].join(","));
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  } else {
    body = (
      <input
        type="search"
        class="text-input"
        placeholder={`Contains…`}
        value={active.contains ?? ""}
        aria-label={attr.label}
        onInput={(e) => {
          const v = (e.target as HTMLInputElement).value;
          if (v === "") clear("contains");
          else set("contains", v);
        }}
      />
    );
  }

  return (
    <div class="filter">
      <div class="filter-head">
        <span class="filter-name">{shownLabel(attr, attr.key)}</span>
        <button
          type="button"
          class="filter-remove"
          onClick={props.onRemove}
          aria-label={`Remove the ${attr.label} filter`}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {/* What the spec means, in view once it is chosen (#213). Picking it folds
          the picker away, and the picker's own hint went with it, so on the
          Filters panel a visitor never saw this. */}
      {attr.description ? <p class="filter-desc">{attr.description}</p> : null}
      {body}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Brand, prime/zoom, image circle — facts about the lens rather than specs. */
function IdentityRow(props: {
  def: IdentityFilter;
  options: { value: string; label: string; count?: number }[];
  query: Query;
  onChange: (q: Query) => void;
  onRemove: () => void;
}) {
  const { def, query } = props;
  const active = filtersFor(query, def.key);
  const chosen = new Set(
    def.op === "in" ? (active.in ?? "").split(",").filter(Boolean)
                    : (active.eq ? [active.eq] : []));

  const apply = (next: Set<string>) => {
    if (next.size === 0) {
      props.onChange(withoutFilter(query, def.key));
      return;
    }
    props.onChange(withFilter(query, {
      key: def.key, op: def.op,
      value: def.op === "in" ? [...next].join(",") : [...next][0]!,
    }));
  };

  return (
    <div class="filter">
      <div class="filter-head">
        <span class="filter-name">{def.label}</span>
        <button type="button" class="filter-remove" onClick={props.onRemove}
                aria-label={`Remove the ${def.label} filter`}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {props.options.length === 0 ? (
        <p class="filter-empty">Nothing to choose from yet.</p>
      ) : (
        <div class="chips" role="group" aria-label={def.label}>
          {props.options.map((o) => (
            <button
              key={o.value}
              type="button"
              class="chip"
              aria-pressed={chosen.has(o.value)}
              onClick={() => {
                const next = new Set(chosen);
                if (next.has(o.value)) next.delete(o.value);
                else if (def.op === "in") next.add(o.value);
                else { next.clear(); next.add(o.value); }
                apply(next);
              }}
            >
              {o.label}
              {/* "Sony (59)", as the mount menu writes it. A space typed here
                  collapsed (the chip is a flex box, which drops a leading
                  space), running the name into the number ("Sony59"). The gap
                  is a margin on .chip-count instead (#213). */}
              {o.count !== undefined ? <span class="chip-count">({o.count})</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A computed column dressed as an attribute, so `FilterRow` renders it with no
 * special case.
 *
 * REQ-FX-016 says a user-built column is filterable "exactly like a stored
 * one". The honest way to deliver that is to make it *be* one at the point of
 * use rather than to write a parallel control that drifts.
 *
 * `display_factor` is null and `display_unit` empty because a computed value
 * has neither — it is whatever the user's arithmetic produced, and inventing a
 * unit for it would be inventing a fact.
 */
function computedAsAttribute(name: string): Attribute {
  return {
    id: -1, key: name, label: humaniseComputed(name),
    short_label: humaniseComputed(name), description: null,
    datatype: "numeric", unit_family: "none", canonical_unit: null,
    display_unit: null, display_factor: null, group: "computed",
    applies_when: null, sort_order: 0, is_core: false, is_expected: false,
    prominence: 2, allow_multiple: false,
    is_filterable: true, is_sortable: true, display_step: null,
    scale: "linear", options: [],
  };
}

/**
 * A filter control for a computed column, which has to find its own bounds.
 *
 * Everything else takes its range from `/stats/ranges`. A computed column is
 * defined by the request, so no precomputed range exists — this asks the server
 * for the extremes by sorting on it, which is already server-side over the full
 * result set (REQ-FX-010). Sampling the visible page instead would give a
 * slider whose ends move as you scroll.
 */
function ComputedFilterRow(props: {
  name: string;
  query: Query;
  onChange: QueryChange;
  onRemove: () => void;
}) {
  const [range, setRange] = useState<Range | null | undefined>(undefined);
  const attr = computedAsAttribute(props.name);
  // The definition, not the filters: refetching whenever a bound moves would
  // make the track jump under the handle being dragged.
  const definition = props.query.computed.map((c) => `${c.name}=${c.formula}`).join("&");

  useEffect(() => {
    const ac = new AbortController();
    setRange(undefined);
    const search = toSearch({ ...props.query, filters: [], columns: [props.name] });
    api
      .computedRange(search, props.name, ac.signal)
      .then(setRange)
      .catch(() => { if (!ac.signal.aborted) setRange(null); });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition, props.name]);

  if (range === undefined) {
    return (
      <div class="filter">
        <div class="filter-head"><span class="filter-name">{props.name}</span></div>
        <p class="filter-empty">Working out the range…</p>
      </div>
    );
  }
  return (
    <FilterRow
      attr={attr}
      range={range ?? undefined}
      query={props.query}
      onChange={props.onChange}
      onRemove={props.onRemove}
    />
  );
}

// ---------------------------------------------------------------------------

/**
 * A filter/column toggle whose hint text survives a touchscreen (REQ-WEB-017;
 * C4, 2026-09-29 QA pass, #286). `title` is a hover-only affordance — a phone,
 * the primary shape (D7), cannot show it — so the hint appears on focus
 * instead, and a tap focuses a `<button>` exactly as a keyboard does. Reuses
 * `.focus-source`, the same caption REQ-WEB-005's cell provenance already
 * shows on focus.
 */
function SpecChip(props: {
  label: string;
  pressed: boolean;
  hint?: string | undefined;
  disabled?: boolean;
  secondary?: boolean;
  onClick: () => void;
}) {
  const [showHint, setShowHint] = useState(false);
  const hintId = useId();
  return (
    <button
      type="button"
      class={`chip${props.secondary ? " chip-empty" : ""}`}
      aria-pressed={props.pressed}
      disabled={props.disabled}
      aria-describedby={props.hint && showHint ? hintId : undefined}
      onFocus={() => setShowHint(true)}
      onBlur={() => setShowHint(false)}
      onClick={props.onClick}
    >
      {props.label}
      {props.hint && showHint ? (
        <span class="focus-source" id={hintId} role="tooltip">
          {props.hint}
        </span>
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------------

export function FilterPanel(props: {
  registry: Registry;
  ranges: Map<string, Range>;
  /** Brands come from live data, never a hardcoded list (REQ-DM-020). */
  mounts: MountList | null;
  /** The same choices counted for the chosen camera (REQ-WEB-035, #207). */
  scoped?: FilterOptions | null;
  query: Query;
  onChange: QueryChange;
  chosen: string[];
  onChooseFilters: (keys: string[]) => void;
}) {
  const { registry, query } = props;
  const filterable = registry.attributes.filter((a) => a.is_filterable);
  const chosen = props.chosen;

  /**
   * The chip grid is for *choosing* a spec; once one is chosen you want its
   * *control*. Showing all the chips permanently pushed the results 745px down
   * the page, so after the first pick the grid folds away behind one button and
   * the active controls take its place.
   */
  const [picking, setPicking] = useState(chosen.length === 0);
  // REQ-WEB-028: everything is offered, the rest collapsed. Derived from
  // `prominence` rather than a list here, so a new attribute lands on the right
  // side of the fold without a frontend change. Prominence is deliberately not
  // completeness (REQ-DM-023) — moving a field across this fold must not touch
  // any published figure.
  const [showUncommon, setShowUncommon] = useState(false);
  const showGrid = picking || chosen.length === 0;
  const core = filterable.filter((a) => a.prominence <= 1);
  const uncommon = filterable.filter((a) => a.prominence > 1);

  // REQ-WEB-035 (#207): counted for the chosen camera, so a brand or an image
  // circle this camera has no lens for is never offered. The site-wide lists
  // stand in only while the scoped ones load.
  const brandOptions = (props.scoped?.brands ?? props.mounts?.brands ?? []).map((b) => ({
    value: b.slug, label: b.name, count: b.lenses,
  }));
  // Ordered by image circle, smallest first, because that is what the rank
  // means (REQ-DM-017) and it is the order a person expects to read them in.
  const coverageOptions = props.scoped
    ? [...props.scoped.coverages].sort((a, b) => a.rank - b.rank)
        .map((c) => ({ value: c.slug, label: c.name, count: c.lenses }))
    : [...(props.mounts?.coverages ?? [])].sort((a, b) => a.rank - b.rank)
        .map((c) => ({ value: c.slug, label: c.name }));
  const identityOptions = (def: IdentityFilter) =>
    def.optionsFrom === "brands" ? brandOptions
      : def.optionsFrom === "coverages" ? coverageOptions
        : (def.options ?? []);

  return (
    <>
      {showGrid ? (
        <>
      {/* The lens's own identity first: brand is the filter people reach for
          before any specification, and it was invisible until now (#72). */}
      <fieldset class="group">
        <legend>The lens itself</legend>
        <div class="chips chips-add">
          {IDENTITY_FILTERS.map((d) => {
            const on = chosen.includes(d.key);
            return (
              <button
                key={d.key}
                type="button"
                class="chip"
                aria-pressed={on}
                onClick={() => {
                  if (on) {
                    props.onChooseFilters(chosen.filter((k) => k !== d.key));
                    props.onChange(withoutFilter(query, d.key));
                  } else {
                    props.onChooseFilters([...chosen, d.key]);
                    setPicking(false);
                  }
                }}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      {byGroupThenName(core).map(([group, attrs]) => (
        <fieldset class="group" key={group}>
          <legend>{titleCase(group)}</legend>
          <div class="chips chips-add">
            {attrs.map((a) => {
              const on = chosen.includes(a.key);
              // A numeric spec with nothing cited cannot filter anything. Still
              // offered — its absence is the invitation to contribute — but
              // visibly secondary so it is not mistaken for a working control.
              const empty =
                (a.datatype === "numeric" || a.datatype === "integer" || a.datatype === "date") &&
                (props.ranges.get(a.key)?.count ?? 0) === 0;
              return (
                <SpecChip
                  key={a.key}
                  label={shortLabel(a, a.key)}
                  pressed={on}
                  secondary={empty}
                  hint={empty ? "Nobody has cited this yet" : (a.description ?? undefined)}
                  onClick={() => {
                    if (on) {
                      props.onChooseFilters(chosen.filter((k) => k !== a.key));
                      props.onChange(withoutFilter(query, a.key));
                    } else {
                      props.onChooseFilters([...chosen, a.key]);
                      setPicking(false);
                    }
                  }}
                />
              );
            })}
          </div>
        </fieldset>
      ))}

      {/* The user's own columns, offered beside the registry's. REQ-FX-016:
          filterable exactly where a stored attribute is — which means in this
          grid, not in a separate control. */}
      {query.computed.length ? (
        <fieldset class="group">
          <legend>Calculated columns</legend>
          <div class="chips chips-add">
            {query.computed.map((c) => {
              const on = chosen.includes(c.name);
              return (
                <SpecChip
                  key={c.name}
                  label={humaniseComputed(c.name)}
                  pressed={on}
                  onClick={() => {
                    if (on) {
                      props.onChooseFilters(chosen.filter((k) => k !== c.name));
                      props.onChange(withoutFilter(query, c.name));
                    } else {
                      props.onChooseFilters([...chosen, c.name]);
                      setPicking(false);
                    }
                  }}
                />
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {uncommon.length ? (
        <div class="uncommon">
          <button type="button" class="uncommon-toggle"
                  aria-expanded={showUncommon}
                  onClick={() => setShowUncommon(!showUncommon)}>
            <span class="disclosure-chevron" aria-hidden="true" />
            {showUncommon ? "Hide less common specs" : `Show ${uncommon.length} less common specs`}
          </button>
          {showUncommon ? byGroupThenName(uncommon).map(([group, attrs]) => (
            <fieldset class="group" key={`u-${group}`}>
              <legend>{titleCase(group)}</legend>
              <div class="chips chips-add">
                {attrs.map((a) => {
                  const on = chosen.includes(a.key);
                  const empty =
                    (a.datatype === "numeric" || a.datatype === "integer" || a.datatype === "date") &&
                    (props.ranges.get(a.key)?.count ?? 0) === 0;
                  return (
                    <SpecChip
                      key={a.key}
                      label={shortLabel(a, a.key)}
                      pressed={on}
                      secondary={empty}
                      hint={empty ? "Nobody has cited this yet" : (a.description ?? undefined)}
                      onClick={() => {
                        if (on) {
                          props.onChooseFilters(chosen.filter((k) => k !== a.key));
                          props.onChange(withoutFilter(query, a.key));
                        } else {
                          props.onChooseFilters([...chosen, a.key]);
                          setPicking(false);
                        }
                      }}
                    />
                  );
                })}
              </div>
            </fieldset>
          )) : null}
        </div>
      ) : null}
        </>
      ) : null}

      {chosen.map((key) => {
        // A computed column filters like any other numeric spec (REQ-FX-016).
        // Its bounds are not in /stats/ranges — it exists only for the request
        // that defines it — so they are fetched from the server, which already
        // sorts it over the whole matched set.
        const computed = query.computed.find((c) => c.name === key);
        if (computed) {
          return (
            <ComputedFilterRow
              key={key}
              name={key}
              query={query}
              onChange={props.onChange}
              onRemove={() => {
                props.onChooseFilters(chosen.filter((k) => k !== key));
                props.onChange(withoutFilter(query, key));
              }}
            />
          );
        }
        const identity = IDENTITY_FILTERS.find((d) => d.key === key);
        if (identity) {
          return (
            <IdentityRow
              key={key}
              def={identity}
              options={identityOptions(identity)}
              query={query}
              onChange={props.onChange}
              onRemove={() => {
                props.onChooseFilters(chosen.filter((k) => k !== key));
                props.onChange(withoutFilter(query, key));
              }}
            />
          );
        }
        const attr = filterable.find((a) => a.key === key);
        if (!attr) return null;
        return (
          <FilterRow
            key={key}
            attr={attr}
            range={props.ranges.get(key)}
            query={query}
            onChange={props.onChange}
            onRemove={() => {
              props.onChooseFilters(chosen.filter((k) => k !== key));
              props.onChange(withoutFilter(query, key));
            }}
          />
        );
      })}

      {/* Below the controls, not above them: an action belongs after the things
          it appends to. */}
      {showGrid ? null : (
        <button type="button" class="add-more" onClick={() => setPicking(true)}>
          + Add another filter
        </button>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

export function ColumnPanel(props: {
  registry: Registry;
  query: Query;
  onChange: (q: Query) => void;
  max: number;
}) {
  const { registry, query } = props;
  const atLimit = query.columns.length >= props.max;
  // REQ-WEB-028 says *pickers*, plural. This one had been left showing all 27
  // attributes flat, which is what made the panel taller than the viewport.
  // The fold follows `prominence`, as the Filters picker does (REQ-WEB-028 as
  // amended 2026-09-09): `is_core` is the completeness denominator (REQ-DM-023)
  // and must not decide what the interface shows.
  const [showUncommon, setShowUncommon] = useState(
    () => registry.attributes.some((a) => a.prominence > 1 && query.columns.includes(a.key)),
  );
  const core = registry.attributes.filter((a) => a.prominence <= 1);
  const uncommon = registry.attributes.filter((a) => a.prominence > 1);

  const chip = (a: Attribute) => {
    const on = query.columns.includes(a.key);
    return (
      <SpecChip
        key={a.key}
        label={shortLabel(a, a.key)}
        pressed={on}
        disabled={!on && atLimit}
        hint={
          !on && atLimit
            ? `Remove one first. ${props.max} specs already fill a row on a phone.`
            : (a.description ?? undefined)
        }
        onClick={() =>
          props.onChange({
            ...query,
            columns: on
              ? query.columns.filter((k) => k !== a.key)
              : [...query.columns, a.key],
          })
        }
      />
    );
  };

  return (
    <>
      <p class="panel-hint">
        Pick up to {props.max} specs to show under each lens. {query.columns.length} picked.
      </p>
      {byGroupThenName(core).map(([group, attrs]) => (
        <fieldset class="group" key={group}>
          <legend>{titleCase(group)}</legend>
          <div class="chips">{attrs.map(chip)}</div>
        </fieldset>
      ))}

      {uncommon.length ? (
        <div class="uncommon">
          <button type="button" class="uncommon-toggle" aria-expanded={showUncommon}
                  onClick={() => setShowUncommon(!showUncommon)}>
            <span class="disclosure-chevron" aria-hidden="true" />
            {showUncommon ? "Hide less common specs" : `Show ${uncommon.length} less common specs`}
          </button>
          {showUncommon
            ? byGroupThenName(uncommon).map(([group, attrs]) => (
                <fieldset class="group" key={`u-${group}`}>
                  <legend>{titleCase(group)}</legend>
                  <div class="chips">{attrs.map(chip)}</div>
                </fieldset>
              ))
            : null}
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------

export function ActiveFilterSummary(props: {
  registry: Registry;
  query: Query;
  onChange: (q: Query) => void;
}) {
  const { registry, query } = props;
  if (query.filters.length === 0) return null;

  const byAttr = new Map<string, Attribute>(registry.attributes.map((a) => [a.key, a]));
  const grouped = new Map<string, typeof query.filters>();
  for (const f of query.filters) {
    const list = grouped.get(f.key) ?? [];
    list.push(f);
    grouped.set(f.key, list);
  }

  return (
    <ul class="active-filters">
      {[...grouped.entries()].map(([key, fs]) => {
        const attr = byAttr.get(key);
        return (
          <li key={key}>
            <button
              type="button"
              class="active-filter"
              onClick={() => props.onChange(withoutFilter(query, key))}
              aria-label={`Remove the ${attr?.label ?? key} filter`}
            >
              <span>{describe(attr, key, fs)}</span>
              <span class="active-filter-x" aria-hidden="true">
                ×
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The active filters in words, one per attribute, worded exactly as the chips
 * are. On a phone the chips are not shown above the list (D29), so the Filters
 * button names the filter when there is only one.
 */
export function activeFilterLabels(registry: Registry, query: Query): string[] {
  const byAttr = new Map<string, Attribute>(registry.attributes.map((a) => [a.key, a]));
  const grouped = new Map<string, typeof query.filters>();
  for (const f of query.filters) {
    const list = grouped.get(f.key) ?? [];
    list.push(f);
    grouped.set(f.key, list);
  }
  return [...grouped.entries()].map(([key, fs]) => describe(byAttr.get(key), key, fs));
}

function describe(attr: Attribute | undefined, key: string, fs: { op: Op; value: string }[]): string {
  // Identity filters have no registry attribute behind them, so their label
  // comes from the definition rather than from `attr`, which is undefined here.
  const identity = IDENTITY_FILTERS.find((d) => d.key === key);
  const name = identity?.label ?? attr?.label ?? key;
  // `withUnit` rather than a trailing unit string, so the chip agrees with the
  // slider and the results table: "f/10", never "10 f".
  // Without a registry step the value is shown as set: it came off the slider's
  // ladder, which already rounded it, and a null step would round it to an
  // integer (#194).
  const num = (v: string) => {
    if (!attr) return v;
    // A date bound is a year, month or day as typed, shown at that precision.
    if (attr.datatype === "date") return formatPartialDate(v);
    const shown = toDisplay(attr, Number(v));
    return withUnit(attr, attr.display_step != null ? formatNumber(shown, attr.display_step) : String(shown));
  };

  // An ordered enum is asked for as "this level or better" (REQ-DM-029).
  if (attr?.datatype === "enum") {
    const atLeast = fs.find((f) => f.op === "gte")?.value;
    if (atLeast !== undefined) {
      const label = attr.options.find((o) => o.value === atLeast)?.label ?? titleCase(atLeast);
      return `${name}: ${label} or better`;
    }
  }

  const gte = fs.find((f) => f.op === "gte")?.value;
  const lte = fs.find((f) => f.op === "lte")?.value;
  if (gte !== undefined && lte !== undefined) return `${name} ${num(gte)}–${num(lte)}`.trim();
  if (lte !== undefined) return `${name} ≤ ${num(lte)}`.trim();
  if (gte !== undefined) return `${name} ≥ ${num(gte)}`.trim();

  const eq = fs.find((f) => f.op === "eq")?.value;
  if (eq !== undefined) {
    const opt = identity?.options?.find((o) => o.value === eq)?.label
      ?? attr?.options.find((o) => o.value === eq)?.label;
    return `${name}: ${opt ?? (eq === "true" ? "Yes" : eq === "false" ? "No" : titleCase(eq))}`;
  }

  const inv = fs.find((f) => f.op === "in")?.value;
  if (inv !== undefined) {
    const labels = inv
      .split(",")
      .map((v) => attr?.options.find((o) => o.value === v)?.label ?? titleCase(v))
      .join(", ");
    return `${name}: ${labels}`;
  }

  const contains = fs.find((f) => f.op === "contains")?.value;
  if (contains !== undefined) return `${name} contains “${contains}”`;
  return name;
}

// ---------------------------------------------------------------------------

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Debounce a value so dragging a slider does not fire a request per pixel. */
export function useDebounced<T>(value: T, ms: number): T {
  const [held, setHeld] = useState(value);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      setHeld(value);
      return;
    }
    const t = setTimeout(() => setHeld(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return held;
}

export { UNKNOWN };

// ---------------------------------------------------------------------------

/**
 * The camera selector — persistent context, not a filter (REQ-WEB-025).
 *
 * It sits above the sort control and outside the filter panel because it is a
 * different kind of thing: the filters narrow what you would like, this decides
 * what can physically go on your camera. Clearing filters does not touch it.
 */
export function CameraBar(props: {
  data: MountList | null;
  camera: Camera;
  onChange: (c: Camera) => void;
}) {
  const { data, camera } = props;
  if (!data) return null;

  const opts = cameraOptions(data);
  const key = (c: Camera) => `${c.mount ?? ""}|${c.format ?? ""}`;

  return (
    <div class="camerabar">
      {/* No visible prefix. "Camera: All mounts" stapled two different things
          together — the label promised a camera and the options delivered a
          mount, and none of them is a camera. The options are self-describing
          noun phrases, so the prefix was only taking width that longer mount
          names will need. The accent rail is what marks this as standing
          context rather than another control. */}
      <label class="visually-hidden" for="camera">
        Which mount and sensor format your lenses have to fit
      </label>
      <select
        id="camera"
        class="select"
        title="Which lenses can go on your camera"
        value={key(camera)}
        onChange={(e) => {
          const chosen = opts.find((o) => key(o.camera) === (e.target as HTMLSelectElement).value);
          if (chosen) props.onChange({ ...chosen.camera, includeSmaller: false });
        }}
      >
        {opts.map((o) => (
          <option key={key(o.camera)} value={key(o.camera)}>
            {o.label} ({o.lenses})
          </option>
        ))}
      </select>

    </div>
  );
}

/**
 * The opt-in for lenses whose image circle is smaller than the chosen sensor.
 *
 * Deliberately **after** the results, not in the control chrome. It is a rare,
 * advanced action — most people never want it — and in the chrome it took a
 * whole row on a phone, pushing control height to 317px against REQ-WEB-019's
 * 281px budget. Here it costs nothing, and it reads better: you get your
 * lenses, then you are told there are more with a caveat.
 */
export function SmallerCoverageOptIn(props: {
  data: MountList | null;
  camera: Camera;
  onChange: (c: Camera) => void;
}) {
  const { count, mountable } = smallerCoverage(props.camera, props.data);
  if (count === 0) return null;
  const format = smallerFormatName(props.camera, props.data);

  // On a mount that will not physically accept them, there is nothing to
  // offer. "Show me lenses that cannot go on my camera" is not a control, it
  // is a trap — so the mount is told about instead (REQ-DM-018).
  if (!mountable) {
    return (
      <p class="smaller-note smaller-note-standalone">
        {count} {format ? `${format} ` : ""}
        {count === 1 ? "lens exists" : "lenses exist"} for this mount, but{" "}
        {count === 1 ? "it does" : "they do"} not fit a{" "}
        {props.data?.coverages.find((c) => c.slug === props.camera.format)?.name.toLowerCase() ??
          "full frame"}{" "}
        body — the mount is keyed to prevent it.
      </p>
    );
  }

  return (
    <div class="smaller-optin">
      <p class="smaller-note">
        {count} more {count === 1 ? "lens covers" : "lenses cover"} only{" "}
        {format ?? "a smaller format"}. {count === 1 ? "It fits" : "They fit"}, but the
        camera crops to that area.
      </p>
      <button
        type="button"
        class="smaller-toggle"
        aria-pressed={props.camera.includeSmaller}
        onClick={() =>
          props.onChange({ ...props.camera, includeSmaller: !props.camera.includeSmaller })
        }
      >
        {props.camera.includeSmaller ? "Hide them" : "Show them anyway"}
      </button>
    </div>
  );
}
